import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, FormControl, FormLabel, FormGroup, FormControlLabel, Checkbox, TextField, Alert, CircularProgress, Box, Typography, Divider, RadioGroup, Radio, Card, CardContent } from '@mui/material';
import Grid from '@mui/material/Grid';

import {
  PictureAsPdf as PdfIcon,
  CalendarToday as CalendarIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ro } from 'date-fns/locale';
import {
  exportService,
  ExportPDFOptions,
  ExportSections
} from '@services/exportService';
import { useAuth } from '@contexts/AuthContext';

interface ExportPDFDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  petId: string;
  petName: string;
}

const ExportPDFDialog: React.FC<ExportPDFDialogProps> = ({
  open,
  onClose,
  onSuccess,
  petId,
  petName
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [exportOptions, setExportOptions] = useState<ExportPDFOptions>({
    petId,
    timeInterval: 'all',
    fromDate: null,
    toDate: null,
    sections: {
      identity: true,
      problems: true,
      visits: true,
      vaccinations: true,
      prescriptions: true,
      documents: true,
      notes: true
    },
    includeImages: true,
    includeOCRText: false,
    language: 'ro'
  });

  const sectionLabels = {
    identity: 'Identitate si Informatii de Baza',
    problems: 'Probleme Medicale Active/Inchise',
    visits: 'Istoric Vizite si Consultatii',
    vaccinations: 'Vaccinari si Deparazitari',
    prescriptions: 'Retete si Medicatie',
    documents: 'Lista Documentelor Atasate',
    notes: 'Note si Observatii'
  };

  const timeIntervalOptions = [
    { value: 'all', label: 'Toate datele disponibile' },
    { value: 'last_year', label: 'Ultimul an' },
    { value: 'last_6_months', label: 'Ultimele 6 luni' },
    { value: 'last_3_months', label: 'Ultimele 3 luni' },
    { value: 'custom', label: 'Interval personalizat' }
  ];

  const handleSectionChange = (section: keyof ExportSections) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setExportOptions(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        [section]: event.target.checked
      }
    }));
  };

  const handleTimeIntervalChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const interval = event.target.value as ExportPDFOptions['timeInterval'];
    setExportOptions(prev => ({
      ...prev,
      timeInterval: interval,
      fromDate: interval !== 'custom' ? null : prev.fromDate,
      toDate: interval !== 'custom' ? null : prev.toDate
    }));
  };

  const handleDateChange = (field: 'fromDate' | 'toDate') => (date: Date | null) => {
    setExportOptions(prev => ({
      ...prev,
      [field]: date
    }));
  };

  const validateOptions = (): string | null => {
    // Check if at least one section is selected
    const hasSelectedSections = Object.values(exportOptions.sections).some(selected => selected);
    if (!hasSelectedSections) {
      return 'Selectati cel putin o sectiune pentru export.';
    }

    // Check custom date range
    if (exportOptions.timeInterval === 'custom') {
      if (!exportOptions.fromDate || !exportOptions.toDate) {
        return 'Completati data de inceput si sfarsitul pentru intervalul personalizat.';
      }
      if (exportOptions.fromDate > exportOptions.toDate) {
        return 'Data de inceput trebuie sa fie inainte de data sfarsitul.';
      }
    }

    return null;
  };

  const handleExport = async () => {
    const validationError = validateOptions();
    if (validationError) {
      setError(validationError);
      return;
    }

    if (!user?.clinicId) {
      setError('Informatii clinic lipsa');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const options: ExportPDFOptions = {
        ...exportOptions,
        clinicId: user.clinicId
      };

      await exportService.exportAnimalRecord(options);
      
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error exporting PDF:', error);
      setError('Eroare la generarea fisei PDF');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setExportOptions({
      petId,
      timeInterval: 'all',
      fromDate: null,
      toDate: null,
      sections: {
        identity: true,
        problems: true,
        visits: true,
        vaccinations: true,
        prescriptions: true,
        documents: true,
        notes: true
      },
      includeImages: true,
      includeOCRText: false,
      language: 'ro'
    });
    setError(null);
    onClose();
  };

  const selectedSectionsCount = Object.values(exportOptions.sections).filter(Boolean).length;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ro}>
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { minHeight: '70vh' }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <PdfIcon color="error" />
            <Typography variant="h6">
              Exporta Fisa PDF pentru {petName}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Time Interval */}
            <Grid size={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                    <CalendarIcon />
                    Interval Temporal
                  </Typography>
                  
                  <FormControl component="fieldset">
                    <RadioGroup
                      value={exportOptions.timeInterval}
                      onChange={handleTimeIntervalChange}
                    >
                      {timeIntervalOptions.map((option) => (
                        <FormControlLabel
                          key={option.value}
                          value={option.value}
                          control={<Radio />}
                          label={option.label}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>

                  {exportOptions.timeInterval === 'custom' && (
                    <Box mt={2}>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <DatePicker
                            label="Data inceput"
                            value={exportOptions.fromDate}
                            onChange={handleDateChange('fromDate')}
                            slotProps={{
                              textField: {
                                fullWidth: true
                              }
                            }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <DatePicker
                            label="Data sfarsit"
                            value={exportOptions.toDate}
                            onChange={handleDateChange('toDate')}
                            slotProps={{
                              textField: {
                                fullWidth: true
                              }
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Sections to Include */}
            <Grid size={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                    <InfoIcon />
                    Sectiuni de Inclus ({selectedSectionsCount} selectate)
                  </Typography>
                  
                  <FormControl component="fieldset" fullWidth>
                    <FormGroup>
                      {Object.entries(sectionLabels).map(([key, label]) => (
                        <FormControlLabel
                          key={key}
                          control={
                            <Checkbox
                              checked={exportOptions.sections[key as keyof ExportSections]}
                              onChange={handleSectionChange(key as keyof ExportSections)}
                            />
                          }
                          label={label}
                        />
                      ))}
                    </FormGroup>
                  </FormControl>
                </CardContent>
              </Card>
            </Grid>

            {/* Additional Options */}
            <Grid size={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Optiuni Suplimentare
                  </Typography>
                  
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={exportOptions.includeImages}
                          onChange={(e) => setExportOptions(prev => ({
                            ...prev,
                            includeImages: e.target.checked
                          }))}
                        />
                      }
                      label="Include imagini si documente vizuale"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={exportOptions.includeOCRText}
                          onChange={(e) => setExportOptions(prev => ({
                            ...prev,
                            includeOCRText: e.target.checked
                          }))}
                        />
                      }
                      label="Include textul extras din documente (OCR)"
                    />
                  </FormGroup>
                </CardContent>
              </Card>
            </Grid>

            {/* Preview Info */}
            <Grid size={12}>
              <Alert severity="info">
                <Typography variant="body2">
                  Fisa PDF va include antetul clinicii, timestamp-ul generarii, paginatie si sectiunile selectate. 
                  Fisierul va fi descarcat automat dupa generare.
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} disabled={loading}>
            Anuleaza
          </Button>
          
          <Button
            variant="contained"
            startIcon={<PdfIcon />}
            onClick={handleExport}
            disabled={loading || selectedSectionsCount === 0}
          >
            {loading ? <CircularProgress size={20} /> : 'Genereaza PDF'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default ExportPDFDialog;
